import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

const DynamicContent = ({ htmlCode, cssCode, javascriptCode, id, scrollDocId, campaignId, kakaoContact }) => {
    const contentRef = useRef(null);
    const [error, setError] = useState(null);

    const injectKakaoButton = useCallback(() => {
        if (!kakaoContact?.enabled || !kakaoContact?.link) return;
        if (!contentRef.current) return;

        // 여러 선택자 시도 (PC/모바일 모두 대응)
        const selectors = [
            '.button-container',
            '.pc-button-container',
            '.mobile-button-container',
            '[class*="button-container"]'
        ];

        let container = null;
        for (const selector of selectors) {
            container = contentRef.current.querySelector(selector);
            if (container) break;
        }

        if (!container) return;

        const quickButton = container.querySelector('.quick-inquiry-button');
        if (!quickButton) return;

        // 이미 추가됐다면 중복 생성 방지
        if (container.querySelector('.kakao-channel-button')) return;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'kakao-channel-button';
        btn.textContent = '카카오톡 상담하기';
        btn.onclick = () => window.open(kakaoContact.link, '_blank', 'noopener,noreferrer');

        // 최소 스타일 (인라인) - 기존 레이아웃을 해치지 않도록 가볍게 적용
        btn.style.padding = '12px 18px';
        btn.style.borderRadius = '999px';
        btn.style.border = 'none';
        btn.style.fontWeight = '700';
        btn.style.fontSize = '15px';
        btn.style.background = 'linear-gradient(135deg, #FFE600, #FFCC00)';
        btn.style.color = '#3A1D1D';
        btn.style.cursor = 'pointer';
        btn.style.boxShadow = '0 6px 16px rgba(0,0,0,0.08)';
        btn.style.minWidth = '120px';

        // 컨테이너와 기존 버튼도 50% 배치되도록 스타일 적용
        container.style.display = 'flex';
        container.style.gap = container.style.gap || '8px';
        container.style.width = container.style.width || '100%';
        quickButton.style.flex = quickButton.style.flex || '1';
        quickButton.style.minWidth = quickButton.style.minWidth || '0';

        container.insertBefore(btn, quickButton);
    }, [kakaoContact]);

    const applyBottomWrapperPadding = useCallback(() => {
        if (!contentRef.current) return;
        const wrappers = contentRef.current.querySelectorAll('.bottom-button-wrapper');
        wrappers.forEach((wrapper) => {
            wrapper.style.paddingLeft = wrapper.style.paddingLeft || '12px';
            wrapper.style.paddingRight = wrapper.style.paddingRight || '12px';
        });
    }, []);

    useEffect(() => {
        if (!contentRef.current) return;

        // 에러 상태 초기화
        setError(null);

        // HTML 코드 삽입 (XSS 방지를 위한 기본적인 검증)
        if (htmlCode && typeof htmlCode === 'string') {
            // 위험한 스크립트 태그 제거
            const sanitizedHtml = htmlCode.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
            contentRef.current.innerHTML = sanitizedHtml;
        }

        // CSS 코드를 head에 삽입
        if (cssCode) {
            const styleId = `dynamic-style-${id}`;
            let existingStyle = document.getElementById(styleId);
            
            if (!existingStyle) {
                existingStyle = document.createElement('style');
                existingStyle.id = styleId;
                document.head.appendChild(existingStyle);
            }
            
            existingStyle.textContent = cssCode;
        }

        // JavaScript 코드 실행 (scrollDocId나 campaignId가 변경되면 다시 실행)
        if (javascriptCode && typeof javascriptCode === 'string') {
            // scrollDocId와 campaignId를 전역으로 노출
            if (scrollDocId) {
                window.scrollDocId = scrollDocId;
            }
            if (campaignId) {
                window.campaignId = campaignId;
            }
            try {
                // 기존 스크립트 태그 제거
                const existingScripts = contentRef.current.querySelectorAll('script');
                existingScripts.forEach(script => script.remove());

                // 전역 변수 충돌 방지: 기존 변수들을 임시로 백업
                const globalVars = ['currentMode', 'isFormOpen', 'openForm', 'closeForm', 'handleSubmit'];
                const backupVars = {};
                
                globalVars.forEach(varName => {
                    if (window[varName] !== undefined) {
                        backupVars[varName] = window[varName];
                        delete window[varName];
                    }
                });

                // JavaScript 코드를 안전하게 실행
                try {
                    // 즉시 실행 함수로 래핑하여 스코프 격리
                    const wrappedCode = `
                        (function() {
                            'use strict';
 
                            let currentMode = '';
                            let isFormOpen = false;
                            
                            try {
                                ${javascriptCode}
                                
                                // 필요한 함수들을 전역으로 노출
                                if (typeof openForm === 'function') window.openForm = openForm;
                                if (typeof closeForm === 'function') window.closeForm = closeForm;
                                if (typeof handleSubmit === 'function') window.handleSubmit = handleSubmit;
                                if (typeof removePhoto === 'function') window.removePhoto = removePhoto;
                                if (typeof removePhotoPC === 'function') window.removePhotoPC = removePhotoPC;
                                if (typeof showPrivacyModal === 'function') window.showPrivacyModal = showPrivacyModal;
                                if (typeof hidePrivacyModal === 'function') window.hidePrivacyModal = hidePrivacyModal;
                                
                            } catch (e) {
                                console.error('Dynamic content script error:', e);
                            }
                        })();
                    `;

                    const script = document.createElement('script');
                    script.textContent = wrappedCode;
                    script.type = 'text/javascript';
                    
                    contentRef.current.appendChild(script);
                } catch (scriptError) {
                    console.error('JavaScript 실행 중 오류 발생:', scriptError);
                    setError('JavaScript 실행 중 오류가 발생했습니다.');
                }
                
                // 백업된 전역 변수들 복원
                Object.keys(backupVars).forEach(varName => {
                    window[varName] = backupVars[varName];
                });
                
            } catch (error) {
                console.error('JavaScript 실행 중 오류 발생:', error);
                setError('JavaScript 실행 중 오류가 발생했습니다.');
            }
        }

        // 카카오 채널 버튼 삽입 (동적 콘텐츠 내 버튼 컨테이너에 삽입)
        // 즉시 시도
        injectKakaoButton();
        applyBottomWrapperPadding();

        // 동적으로 추가되는 요소를 감지하기 위한 MutationObserver
        const observer = new MutationObserver(() => {
            injectKakaoButton();
            applyBottomWrapperPadding();
        });

        if (contentRef.current) {
            observer.observe(contentRef.current, {
                childList: true,
                subtree: true
            });
        }

        // 지연 후에도 다시 시도 (동적 스크립트가 늦게 실행될 수 있음)
        const timeout1 = setTimeout(() => {
            injectKakaoButton();
            applyBottomWrapperPadding();
        }, 500);

        const timeout2 = setTimeout(() => {
            injectKakaoButton();
            applyBottomWrapperPadding();
        }, 1500);

        // 컴포넌트 언마운트 시 정리
        return () => {
            observer.disconnect();
            clearTimeout(timeout1);
            clearTimeout(timeout2);

            if (contentRef.current) {
                contentRef.current.innerHTML = '';
            }
            
            // CSS 스타일 제거
            const styleId = `dynamic-style-${id}`;
            const existingStyle = document.getElementById(styleId);
            if (existingStyle) {
                existingStyle.remove();
            }
            
            // scrollDocId와 campaignId 정리
            if (window.scrollDocId) {
                delete window.scrollDocId;
            }
            if (window.campaignId) {
                delete window.campaignId;
            }
            
        };
    }, [htmlCode, cssCode, javascriptCode, id, scrollDocId, campaignId, injectKakaoButton, applyBottomWrapperPadding]);

    return (
        <Container>
            {error && (
                <ErrorMessage>
                    {error}
                </ErrorMessage>
            )}
            
            {/* HTML 콘텐츠를 렌더링할 div */}
            <div 
                ref={contentRef} 
                id={`dynamic-content-${id}`}
                className="dynamic-content"
            />
        </Container>
    );
};

const Container = styled.div`
    width: 100%;
    
    .dynamic-content {
        width: 100%;
    }
    
    /* 동적으로 삽입된 스타일이 제대로 적용되도록 보장 */
    .dynamic-content * {
        box-sizing: border-box;
    }
`;

const ErrorMessage = styled.div`
    background-color: #fee2e2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 14px;
    text-align: center;
`;

DynamicContent.propTypes = {
    htmlCode: PropTypes.string,
    cssCode: PropTypes.string,
    javascriptCode: PropTypes.string,
    id: PropTypes.string.isRequired,
    scrollDocId: PropTypes.string,
    campaignId: PropTypes.string,
    kakaoContact: PropTypes.shape({
        enabled: PropTypes.bool,
        link: PropTypes.string
    })
};

DynamicContent.defaultProps = {
    htmlCode: '',
    cssCode: '',
    javascriptCode: '',
    kakaoContact: null
};

export default DynamicContent;
